/**
 * Single place that decides which {@link SessionsRepository} the app uses.
 *
 * When `VITE_SESSIONS_API_URL` is configured we use the live
 * {@link ApiSessionsRepository} (reads `GET /sessions/mine`, submits lifecycle
 * events to `POST /sessions/state`). Otherwise we fall back to the network-free
 * {@link MockSessionsRepository} so the local demo still works without a
 * deployed backend. Call sites are unaffected — they depend on the interface.
 */

import { MockSessionsRepository } from './mock-sessions-repository'
import { ApiSessionsRepository } from './api-sessions-repository'
import type { SessionsRepository } from './sessions-repository'

let instance: SessionsRepository | null = null

/** True when a Sessions API base URL is configured for the live repository. */
function isApiConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SESSIONS_API_URL)
}

/** Diagnostic snapshot of which repository is active and its endpoint. */
export interface ActiveRepositoryInfo {
  /** `'api'` when `VITE_SESSIONS_API_URL` is set, otherwise `'mock'`. */
  mode: 'api' | 'mock'
  /** Normalized base URL for the live API, or `null` in mock mode. */
  baseUrl: string | null
}

/**
 * Report whether the live API or the mock repository is active (and the API
 * base URL when applicable). Used by the temporary Sessions data-source
 * diagnostic banner; safe to call without instantiating the repository.
 */
export function getActiveRepositoryInfo(): ActiveRepositoryInfo {
  const url = import.meta.env.VITE_SESSIONS_API_URL
  if (url) {
    return { mode: 'api', baseUrl: String(url).replace(/\/$/, '') }
  }
  return { mode: 'mock', baseUrl: null }
}

/** The process-wide repository singleton (lazily created). */
export function getSessionsRepository(): SessionsRepository {
  if (!instance) {
    instance = isApiConfigured()
      ? new ApiSessionsRepository()
      : new MockSessionsRepository()
  }
  return instance
}

/** Test/demo hook: override the active repository (e.g. force the API impl). */
export function setSessionsRepository(repo: SessionsRepository): void {
  instance = repo
}

export { MockSessionsRepository, ApiSessionsRepository }
